import * as types from '@/store/mutation-types';
import moment from 'moment';
import api from '@/store/api';
import { logAction } from '../logActions';
import { editingAllowed } from '../actions';
import router from '../../router';
import * as treeHelpers from '@/common/treeHelpers';
import * as stageHelpers from '@/common/stageHelpers';
import * as colourHelpers from '@/common/colourHelpers';
import log from '@/logger';

export const selectStage = ({ commit, state }, stage) => {
  log.debug('actions:selectStage', stage);

  if (stage && stage._id) {
    if (router.currentRoute.params._id !== stage.projectId || router.currentRoute.params.stageId !== stage._id) {
      router.push({
        name: 'ProjectPlanStage',
        params: { _id: stage.projectId, stageId: stage._id },
        query: router.currentRoute.query,
      });
    }

    commit(types.selectDiaryEntry, null);
    commit(types.selectPart, null);
    commit(types.selectStage, stage);
  } else {
    commit(types.selectStage, null);
  }
};

export const undoDeleteStage = async ({ commit, state, dispatch }, { stage, undoStrategy, parent, index }) => {
  var project = state.currentProject;
  var realStage = stageHelpers.findStage(state, stage._id);
  await dispatch('saveStageProperties', {
    updateStage: realStage,
    newStageProperties: { archived: false },
    undoStrategy: 'undo',
  });
  await commit(types.addStage, {
    newStage: realStage,
    parent: treeHelpers.findChildInTree(project, parent._id).children,
    index: index,
  });
};

export const redoDeleteStage = async ({ commit, state, dispatch }, { stage, undoStrategy }) => {
  var realStage = stageHelpers.findStage(state, stage._id);
  await dispatch('deleteStage', { stage: realStage, undoStrategy: 'redo' });
};

export const deleteStage = async ({ commit, state, dispatch }, { stage, undoStrategy }) => {
  if (!editingAllowed(state)) {
    return;
  }

  var project = state.currentProject;
  var parent = treeHelpers.findParent(project, stage._id);
  var index = treeHelpers.indexOfChild(parent.children, stage._id);

  commit(types.appendUndo, { action: 'deleteStage', data: { stage, parent, index }, undoStrategy: undoStrategy });

  await archiveChildren(
    dispatch,
    commit,
    state,
    treeHelpers.findChildInTree(project, stage._id).children,
    undoStrategy
  );
  commit(types.deleteStage, { parent: parent.children, index });
  logAction({ commit, state }, { project: project, object: stage, reason: 'Deleted Stage' });
  commit(types.setStageProperties, { updateStage: stage, newStageProperties: { archived: true } });
  commit(types.selectStage, null);
  await api
    .saveStage(stage)
    .then((savedStage) => {
      commit(types.setStageProperties, { updateStage: stage, newStageProperties: savedStage });
    })
    .catch((e) => {
      log.error('save stage Error', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });

  await dispatch('clearUndo');

  return api
    .saveProject(project)
    .then((savedProject) => {
      commit(types.setProjectProperties, { updateProject: project, newProjectProperties: savedProject });
      // treeHelpers.outputTree(project.children, 0);

      return Promise.resolve(project);
    })
    .catch((e) => {
      log.error('save project error', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

async function archiveChildren(dispatch, commit, state, parent, undoStrategy) {
  for (var child of parent) {
    if (child.type === 'Part') {
      var part = state.parts[child._id];
      await dispatch('savePartProperties', {
        updatePart: part,
        newPartProperties: { archived: true },
        undoStrategy: undoStrategy || 'append',
      });
    } else if (child.type === 'Stage') {
      var stage = stageHelpers.findStage(state, child._id);
      await dispatch('saveStageProperties', {
        updateStage: stage,
        newStageProperties: { archived: true },
        undoStrategy: undoStrategy || 'append',
      });
      await archiveChildren(dispatch, commit, state, child.children, undoStrategy);
    }
  }
}

export const duplicateStage = async ({ commit, state, dispatch }, { stage, undoStrategy }) => {
  commit(types.setIsBusy, 1);
  var parent = treeHelpers.findParent(state.currentProject, stage._id);

  var suffix = stageHelpers.generateTitleSuffix(stage.title);
  var newTree = await duplicateStageRecurse(commit, state, stage, suffix);

  var index = treeHelpers.indexOfChild(parent.children, stage._id);
  commit(types.addStage, { newStage: newTree, parent: parent.children, index });
  await api.saveProject(state.currentProject);
  await dispatch('clearUndo');
  commit(types.setIsBusy, -1);
};

const duplicateStageRecurse = async function (commit, state, stage, replaceTitle) {
  var newStage = await api.createStage(state.currentProject._id, stage);
  if (replaceTitle) {
    newStage.title = replaceTitle;
  }
  commit(types.addSyncedStage, newStage);

  var newSubTree = { _id: newStage._id, title: newStage.title, type: 'Stage', children: [] };
  var oldSubtree = treeHelpers.findChildren(state.currentProject.children, stage._id);

  for (var child of oldSubtree) {
    if (child.type === 'Stage') {
      var childStage = stageHelpers.findStage(state, child._id);
      var newTree = await duplicateStageRecurse(commit, state, childStage);
      newSubTree.children.push(newTree);
    } else if (child.type === 'Part') {
      var childPart = state.parts[child._id];
      var newPart = await api.createPart(state.currentProject._id, childPart);
      commit(types.addSyncedPart, newPart);
      newSubTree.children.push({ _id: newPart._id, type: 'Part', title: newPart.title });
    }
  }

  return newSubTree;
};

export const undoAddStage = async ({ commit, state, dispatch }, { stage }) => {
  var realStage = stageHelpers.findStage(state, stage._id);
  await dispatch('deleteStage', { stage: realStage, undoStrategy: 'undo' });
};

export const redoAddStage = async ({ commit, state, dispatch }, { parent, stage, properties }) => {
  var realStage = stageHelpers.findStage(state, stage._id);
  await dispatch('saveStageProperties', {
    updateStage: realStage,
    newStageProperties: { archived: false },
    undoStrategy: 'redo',
  });
  await dispatch('addStage', { stage: realStage, parent, undoStrategy: 'redo' });
};

export const addStage = ({ commit, state }, { stage, parent, properties, undoStrategy }) => {
  if (!editingAllowed(state)) {
    return;
  }

  var project = state.currentProject;

  return (stage ? Promise.resolve(stage) : api.createStage(project._id, properties))
    .then((stage) => {
      var parentList = treeHelpers.findChildInTree(project, parent._id);
      if (parentList) {
        parentList = parentList.children;
      }
      commit(types.addStage, { newStage: stage, parent: parentList });
      commit(types.appendUndo, { action: 'addStage', data: { stage, parent }, undoStrategy: undoStrategy });

      logAction({ commit, state }, { project: project, object: stage, reason: 'Added Stage' });
      return Promise.all([Promise.resolve(stage), api.saveProject(project)]);
    })
    .then(([newStage, savedProject]) => {
      var update = { updateProject: project, newProjectProperties: savedProject };
      commit(types.setProjectProperties, update);

      return Promise.resolve(newStage);
    })
    .catch((e) => {
      log.error('stageActions:addStage', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const addSyncedStage = ({ commit, state }, stage) => {
  log.debug('actions:addSyncedStage', stage);

  commit(types.addStage, { newStage: stage });
};

export const undoSaveStageProperties = async ({ commit, state, dispatch }, update) => {
  var undoProperties = update.newStageProperties;
  for (var property in undoProperties) {
    undoProperties[property] = update.updateStage[property];
  }
  var stage = stageHelpers.findStage(state, update.updateStage._id);
  await dispatch('saveStageProperties', {
    updateStage: stage,
    newStageProperties: undoProperties,
    undoStrategy: 'undo',
  });
};

export const redoSaveStageProperties = async ({ commit, state, dispatch }, update) => {
  var stage = stageHelpers.findStage(state, update.updateStage._id);
  await dispatch('saveStageProperties', {
    updateStage: stage,
    newStageProperties: update.newStageProperties,
    undoStrategy: 'redo',
  });
};

export const saveStageProperties = ({ commit, state }, update) => {
  log.debug('actions:saveStageProperties', update);

  if (!editingAllowed(state)) {
    return;
  }

  commit(types.appendUndo, { action: 'saveStageProperties', data: update, undoStrategy: update.undoStrategy });

  logAction(
    { commit, state },
    {
      project: state.currentProject,
      object: update.updateStage,
      reason: 'Updated Stage Properties',
      update: update.newStageProperties,
    }
  );
  commit(types.setStageProperties, update);
  return api
    .saveStage(update.updateStage)
    .then((savedStage) => {
      update.newStageProperties = savedStage;
      commit(types.setStageProperties, update);
      return update.updateStage;
    })
    .catch((e) => {
      log.error('saveStageProperties', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const toggleStageChildren = ({ commit, state }, stage) => {
  var showChildren;
  if (stage.showChildren === true) {
    showChildren = false;
  } else {
    showChildren = true;
    var children = treeHelpers.findChildren(state.currentProject.children, stage._id);
    if (children) {
      for (var child of children) {
        var childStage = state.stages[child._id];
        if (childStage && childStage.showChildren === true) {
          commit(types.setStageProperties, { updateStage: childStage, newStageProperties: { showChildren: false } });
        }
      }
    }
  }
  commit(types.setStageProperties, { updateStage: stage, newStageProperties: { showChildren: showChildren } });
};

export const closeAllTopLevelStages = ({ commit, state }) => {
  const newStages = {};
  Object.entries(state.stages || {}).forEach(([stageId, stage]) => {
    if (typeof stage === 'object' && stage != null) {
      newStages[stageId] = { ...stage, showChildren: false };
    }
  });
  commit(types.setStages, newStages);
};

export const openAllTopLevelStages = ({ commit, state }) => {
  const newStages = {};
  Object.entries(state.stages || {}).forEach(([stageId, stage]) => {
    if (typeof stage === 'object' && stage != null) {
      newStages[stageId] = { ...stage, showChildren: true };
    }
  });
  commit(types.setStages, newStages);
};

export const syncStageProperties = ({ commit, state }, update) => {
  log.debug('actions:syncStageProperties', update);

  commit(types.setStageProperties, update);
};

export const setStageProperties = ({ commit, state }, update) => {
  log.debug('actions:setStageProperties', update);

  if (!editingAllowed(state)) {
    return;
  }

  commit(types.setStageProperties, update);
};

export const undoMoveStageChildren = async ({ commit, state, dispatch }, update) => {};

export const redoMoveStageChildren = async ({ commit, state, dispatch }, update) => {};

export const moveStageChildren = async ({ commit, state, dispatch }, update) => {
  log.debug('actions:moveStageChildren', update);
  commit(types.appendUndo, { action: 'moveStageChildren', data: update });

  logAction(
    { commit, state },
    { project: state.currentProject, object: update.updateStage, reason: 'Updated Children Properties' }
  );
  await dispatch('moveStageChildrenRecurse', update);
};

export const moveStageChildrenRecurse = async ({ commit, state, dispatch }, update) => {
  var children = update.parent;
  var updates = [];

  for (var child of children) {
    if (child.type === 'Stage') {
      updates.push(
        dispatch('moveStageChildrenRecurse', {
          parent: treeHelpers.findChildren(children, child._id),
          relativeStartdays: update.relativeStartdays,
          undoStrategy: 'append',
        })
      );
    } else {
      var part = state.parts[child._id];
      var startdate = moment(part.startdate).add(update.relativeStartdays, 'days').format('YYYY-MM-DD');

      var expectedDonedate = moment(startdate);
      if (expectedDonedate.isoWeekday() >= 6) {
        expectedDonedate.add(8 - expectedDonedate.isoWeekday(), 'day');
      }
      expectedDonedate = expectedDonedate.isoAddWeekdaysFromSet(part.time - 1, [1, 2, 3, 4, 5]).format('YYYY-MM-DD');

      var newPartProperties = {
        startdate: startdate,
        expectedDonedate: expectedDonedate,
        controldate: expectedDonedate,
        calendarTime: moment(expectedDonedate).diff(startdate, 'day') + 1,
      };

      updates.push(
        dispatch('savePartProperties', {
          updatePart: part,
          newPartProperties: Object.assign({}, newPartProperties),
          undoStrategy: 'append',
        })
      );
    }
  }
  return Promise.all(updates);
};

export const undoUpdateStagePropertyWithClear = ({ commit, state, dispatch }, update) => {};

export const updateStagePropertyWithClear = async ({ commit, state, dispatch }, update) => {
  log.debug('actions:updateStagePropertyWithClear', update);

  var parent = treeHelpers.findChildren(state.currentProject.children, update.stage._id);
  update.parent = parent;

  commit(types.appendUndo, { action: 'updateStagePropertyWithClear', data: update });

  await dispatch('updateStagePropertyWithClearRecurse', update);
};

export const updateStagePropertyWithClearRecurse = async ({ commit, state, dispatch }, update) => {
  var updates = [Promise.resolve()];
  var children = update.parent;

  var u = { updateStage: state.stages[update.stage._id], newStageProperties: {}, undoStrategy: 'append' };
  u.newStageProperties[update.property] = update[update.property];
  updates.push(dispatch('saveStageProperties', u));

  for (var child of children) {
    if (child.type === 'Stage') {
      u = {
        parent: treeHelpers.findChildren(children, child._id),
        property: update.property,
        stage: child,
        undoStrategy: 'append',
      };
      u[update.property] = '';
      updates.push(dispatch('updateStagePropertyWithClearRecurse', u));
    } else {
      var part = state.parts[child._id];
      u = { updatePart: part, newPartProperties: {}, undoStrategy: 'append' };
      u.newPartProperties[update.property.replace('stage', 'part')] = '';
      updates.push(dispatch('savePartProperties', u));
    }
  }
  return Promise.all(updates);
};

export const undoUpdateChildrenColours = ({ commit, state, dispatch }, update) => {};

export const updateChildrenColours = async ({ commit, state, dispatch }, update) => {
  commit(types.appendUndo, { action: 'updateChildrenColours', data: update });

  await dispatch('updateChildrenColoursRecurse', update);
};

export const updateChildrenColoursRecurse = async ({ commit, state, dispatch }, update) => {
  var updates = [Promise.resolve()];
  var children = update.parent;
  updates.push(
    dispatch('saveStageProperties', {
      updateStage: state.stages[update.stage._id],
      newStageProperties: { colour: update.colour },
      undoStrategy: 'append',
    })
  );

  for (var child of children) {
    if (child.type === 'Stage') {
      updates.push(
        dispatch('updateChildrenColoursRecurse', {
          parent: treeHelpers.findChildren(children, child._id),
          stage: child,
          colour: colourHelpers.shadeColour(update.colour, 0.4),
          undoStrategy: 'append',
        })
      );
    } else {
      var part = state.parts[child._id];
      updates.push(
        dispatch('savePartProperties', {
          updatePart: part,
          newPartProperties: { colour: colourHelpers.shadeColour(update.colour, 0.4) },
          undoStrategy: 'append',
        })
      );
    }
  }
  return Promise.all(updates);
};

export const showAllChildren = async ({ commit, state, dispatch }, stage) => {
  await dispatch('showAllChildrenRecurse', {
    parent: treeHelpers.findChildren(state.currentProject.children, stage._id),
    stage,
  });
};

export const showAllChildrenRecurse = async ({ commit, state, dispatch }, update) => {
  var children = update.parent;

  for (var child of children) {
    if (child.type === 'Stage') {
      dispatch('showAllChildrenRecurse', { parent: treeHelpers.findChildren(children, child._id), stage: child });
    }
  }
  dispatch('setStageProperties', {
    updateStage: state.stages[update.stage._id],
    newStageProperties: { showChildren: true },
  });
};



// WEBPACK FOOTER //
// ./src/store/stage/stageActions.js